/**
 * TourOverlay — renders the spotlight highlight + tooltip for the active tour step.
 *
 * Uses a full-screen SVG mask to create the cutout effect around the target element,
 * with a positioned tooltip alongside it.
 */
import * as React from 'react';
import { ArrowRight, ArrowLeft, X } from '@phosphor-icons/react';
import { useTour } from './TourProvider';
import s from './tour.module.scss';

const PAD = 8; // px padding around the highlighted element
const TOOLTIP_GAP = 12; // px gap between highlight and tooltip
const TOOLTIP_MAX_W = 320;

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export function TourOverlay() {
  const {
    step,
    currentStep,
    totalSteps,
    next,
    prev,
    dismiss,
    transitionMs,
    enterMs,
  } = useTour();
  const [targetRect, setTargetRect] = React.useState<Rect | null>(null);
  const [tooltipStyle, setTooltipStyle] = React.useState<React.CSSProperties>(
    {},
  );
  const tooltipRef = React.useRef<HTMLDivElement>(null);

  // Find + measure target element
  React.useEffect(() => {
    if (!step) return;
    let frame = 0;
    let resizeObserver: ResizeObserver | undefined;
    let mutationObserver: MutationObserver | undefined;

    function findVisibleTarget() {
      const candidates = Array.from(
        document.querySelectorAll<HTMLElement>(`[data-tour="${step!.target}"]`),
      );
      return (
        candidates.find((candidate) => {
          const rect = candidate.getBoundingClientRect();
          const styles = window.getComputedStyle(candidate);
          return (
            rect.width > 0 &&
            rect.height > 0 &&
            styles.display !== 'none' &&
            styles.visibility !== 'hidden' &&
            !candidate.closest('[aria-hidden="true"]')
          );
        }) ?? null
      );
    }

    function measure() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const el = findVisibleTarget();
        if (!el) {
          setTargetRect(null);
          return;
        }
        const r = el.getBoundingClientRect();
        setTargetRect({
          top: r.top - PAD,
          left: r.left - PAD,
          width: r.width + PAD * 2,
          height: r.height + PAD * 2,
        });
      });
    }

    measure();
    if ('ResizeObserver' in window) {
      const el = findVisibleTarget();
      if (el) {
        resizeObserver = new ResizeObserver(measure);
        resizeObserver.observe(el);
      }
    }
    if ('MutationObserver' in window) {
      mutationObserver = new MutationObserver(measure);
      mutationObserver.observe(document.body, {
        attributes: true,
        childList: true,
        subtree: true,
        attributeFilter: ['class', 'style', 'data-loading', 'aria-hidden'],
      });
    }
    // Re-measure on resize/scroll
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      cancelAnimationFrame(frame);
      resizeObserver?.disconnect();
      mutationObserver?.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [step]);

  // Position tooltip after target rect is known
  React.useEffect(() => {
    if (!targetRect || !step) {
      setTooltipStyle({ opacity: 0 });
      return;
    }

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const style: React.CSSProperties = {
      position: 'fixed',
      maxWidth: TOOLTIP_MAX_W,
      opacity: 1,
    };

    const placement = step.placement;

    if (placement === 'bottom') {
      style.top = targetRect.top + targetRect.height + TOOLTIP_GAP;
      style.left = targetRect.left;
      // Keep tooltip on screen
      if ((style.left as number) + TOOLTIP_MAX_W > vw - 16) {
        style.left = vw - TOOLTIP_MAX_W - 16;
      }
    } else if (placement === 'top') {
      style.bottom = vh - targetRect.top + TOOLTIP_GAP;
      style.left = targetRect.left;
      if ((style.left as number) + TOOLTIP_MAX_W > vw - 16) {
        style.left = vw - TOOLTIP_MAX_W - 16;
      }
    } else if (placement === 'right') {
      style.top = targetRect.top;
      style.left = targetRect.left + targetRect.width + TOOLTIP_GAP;
      if ((style.left as number) + TOOLTIP_MAX_W > vw - 16) {
        style.left = vw - TOOLTIP_MAX_W - 16;
      }
    } else {
      // left
      style.top = targetRect.top;
      style.right = vw - targetRect.left + TOOLTIP_GAP;
    }

    setTooltipStyle(style);
  }, [targetRect, step]);

  // Keyboard: Escape to dismiss, ArrowRight/Enter to next, ArrowLeft to prev
  React.useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') dismiss();
      if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      if (e.key === 'ArrowLeft') prev();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [dismiss, next, prev]);

  if (!step) return null;

  const isFirst = currentStep === 0;
  const isLast = currentStep === totalSteps - 1;

  return (
    <div
      className={s.overlay}
      style={
        {
          '--tour-transition-ms': `${transitionMs}ms`,
          '--tour-enter-ms': `${enterMs}ms`,
        } as React.CSSProperties
      }
    >
      {/* SVG mask with cutout */}
      <svg className={s.backdrop}>
        <defs>
          <mask id="tour-mask">
            <rect x="0" y="0" width="100%" height="100%" fill="white" />
            {targetRect && (
              <rect
                x={targetRect.left}
                y={targetRect.top}
                width={targetRect.width}
                height={targetRect.height}
                rx="10"
                fill="black"
              />
            )}
          </mask>
        </defs>
        <rect
          x="0"
          y="0"
          width="100%"
          height="100%"
          fill="rgba(0,0,0,0.6)"
          mask="url(#tour-mask)"
        />
      </svg>

      {/* Highlight ring around target */}
      {targetRect && (
        <div
          className={s.highlightRing}
          style={{
            top: targetRect.top,
            left: targetRect.left,
            width: targetRect.width,
            height: targetRect.height,
          }}
        />
      )}

      {/* Tooltip */}
      <div ref={tooltipRef} className={s.tooltip} style={tooltipStyle}>
        <div className={s.tooltipHeader}>
          <span className={s.tooltipStep}>
            {currentStep + 1} of {totalSteps}
          </span>
          <button
            type="button"
            className={s.tooltipClose}
            onClick={dismiss}
            aria-label="Close tour"
          >
            <X size={14} />
          </button>
        </div>
        <h3 className={s.tooltipTitle}>{step.title}</h3>
        <p className={s.tooltipDesc}>{step.description}</p>

        {/* Progress dots */}
        <div className={s.tooltipFooter}>
          <div className={s.dots}>
            {Array.from({ length: totalSteps }).map((_, i) => (
              <span
                key={i}
                className={s.dot}
                data-active={i === currentStep ? '' : undefined}
              />
            ))}
          </div>
          <div className={s.tooltipActions}>
            {!isFirst && (
              <button
                type="button"
                className={s.tooltipBtnGhost}
                onClick={prev}
              >
                <ArrowLeft size={13} />
                Back
              </button>
            )}
            {isFirst && (
              <button
                type="button"
                className={s.tooltipBtnGhost}
                onClick={dismiss}
              >
                Skip
              </button>
            )}
            <button
              type="button"
              className={s.tooltipBtnPrimary}
              onClick={next}
            >
              {isLast ? 'Done' : 'Next'}
              {!isLast && <ArrowRight size={13} />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
